/**
 * useEntities — Hook for loading registered legal entities.
 * Fetches from /api/v1/entities and caches for the session.
 * Used by all create forms that need an entity_code selector.
 */

import { useState, useEffect } from 'react';
import { API_V1, authFetch } from '../constants';

interface Entity {
  entity_code: string;
  entity_name: string;
  country: string;
  functional_currency: string;
}

let _cached: Entity[] | null = null;
let _promise: Promise<void> | null = null;

function _fetch(): Promise<void> {
  if (_promise) return _promise;
  _promise = authFetch(`${API_V1}/entities`)
    .then(r => r.ok ? r.json() : null)
    .then(data => {
      if (Array.isArray(data)) _cached = data;
      else if (data?.entities) _cached = data.entities;
      else if (data?.items) _cached = data.items;
    })
    .catch(() => {})
    .finally(() => { _promise = null; });
  return _promise;
}

export function useEntities() {
  const [entities, setEntities] = useState<Entity[]>(_cached || []);

  useEffect(() => {
    if (_cached) {
      setEntities(_cached);
      return;
    }
    _fetch().then(() => {
      if (_cached) setEntities(_cached);
    });
  }, []);

  return entities;
}
